import Vue from 'vue'
import {
  ProjectConfig,
  ProjectConfigExtra,
  ProjectModel,
  ProjectSegmentModel,
  SpeakerCollection,
  SpeakerVoice,
} from '@ve-pro/api/project/type'
import { updateProjectConfig as _updateProjectConfig } from '@ve-pro/api/project'
import { useProjectStore } from '.'
import { useTrackStore } from '../tracks'
import {
  RatioTypeUnion,
  SegmentCollection,
  SegmentMaterial,
  SegmentText,
  SegmentVideo,
} from '@ve-pro/types/track.type'
import {
  getGroupBySegment,
  isSegmentGroupChild,
} from '../tracks/segment/segment-group'
import { getAllSegments } from '../tracks/segment'
import { SegmentType, SourceSegmentIdBizType } from '@ve-pro/enums/segment'
import { SpeechVoiceModel } from '@/api/ai/index.type'
// import { Speaker } from '@ve-pro/api/project/type'
import { t } from '@ve-pro/ve/lang'
import { cloneDeep, uniq } from 'lodash-es'
import { isSegmentSubtitle } from '../tracks/segment/segment-subtitle'
import { RatioType } from 'video-editor-renderer'
import { createSpeakerVoice, isSettledSpeakerVoice } from '../speaker/utils'
import { SpeakerType } from '@ve-pro/enums/speaker'
import { VoiceItem } from '@pt/api/ai/index.type'
import { SpeakerUserUpload, Speaker } from '@ve-pro/api/project/type'

export function resolveConfigCopy(
  config: ProjectConfig,
): ProjectConfig & ProjectConfigExtra {
  const copyConfig = cloneDeep(config)
  if (copyConfig.aiRemove) {
    Object.values(copyConfig.aiRemove).forEach((removeItem) => {
      removeItem.items.forEach((item) => {
        item.imageUrl = ''
      })
    })
  }

  const taskIds: ProjectConfigExtra['async_task_ids'] = []
  Object.values(copyConfig.aiRemove).forEach((aiRemoveItem) => {
    aiRemoveItem.taskId
    aiRemoveItem.taskIdAll
    if (aiRemoveItem.taskId) {
      taskIds.push(aiRemoveItem.taskId)
    }
    if (aiRemoveItem.taskIdAll) {
      taskIds.push(aiRemoveItem.taskIdAll)
    }
  })
  copyConfig.task.forEach((t) => {
    taskIds.push(t.taskId)
  })

  return { ...copyConfig, async_task_ids: uniq(taskIds) }
}

export function updateProjectConfig(
  id: ProjectModel['id'],
  config: ProjectConfig,
): ReturnType<typeof _updateProjectConfig> {
  return _updateProjectConfig({
    id,
    config: JSON.stringify(resolveConfigCopy(config)),
  })
}

/** 原始语言 */
export const DEFAULT_LANGUAGE = 'default'

// 检查坑位是否开启多语言
export function isSegmentEnableLanguages(
  id: ProjectSegmentModel['id'],
): boolean {
  const projectStore = useProjectStore()

  return projectStore.configs.enableLanguagesSegmentIds.includes(id)
}

// 根据环境判断元件是否开启多语言
// 该方法用于替换isSegmentEnableLanguages
export function isMultilingualEnabled(segment: SegmentCollection): boolean {
  const projectStore = useProjectStore()

  // 如果是字幕，且是在视频编辑器
  if (isSegmentSubtitle(segment) && !projectStore.isReplaceProject) {
    return projectStore.configs.enableAllSubtitleLanguages
  }

  if (isSegmentGroupChild(segment)) {
    return projectStore.configs.enableLanguagesSegmentIds.includes(segment.pid)
  }

  if (isSegmentSubtitle(segment) && projectStore.isReplaceProject) {
    return projectStore.configs.enableLanguagesSegmentIds.includes(
      segment.source_segment_id,
    )
  }

  return projectStore.configs.enableLanguagesSegmentIds.includes(segment.id)
}

export function addEnableLanguagesSegmentIds(
  ids: ProjectSegmentModel['id'][],
): void {
  const projectStore = useProjectStore()

  ids.forEach((id) => {
    if (!projectStore.configs.enableLanguagesSegmentIds.includes(id)) {
      projectStore.configs.enableLanguagesSegmentIds.push(id)
    }
  })
}

export function removeEnableLanguagesSegmentIds(
  id: ProjectSegmentModel['id'],
): void {
  const projectStore = useProjectStore()

  projectStore.configs.enableLanguagesSegmentIds =
    projectStore.configs.enableLanguagesSegmentIds.filter((t) => t !== id)
}

// 通过source_segment_id找到源头，返回是否开启多语言
export function isSourceSegmentEnableLanguages(
  segment: SegmentCollection,
): boolean {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()
  if (isSegmentGroupChild(segment)) {
    const group = getGroupBySegment(segment)
    if (!group) {
      return false
    }
    return (
      isSegmentEnableLanguages(group.id) || isSegmentEnableLanguages(segment.id)
    )
  }

  // 如果是字幕，且是视频编辑器
  if (isSegmentSubtitle(segment) && !projectStore.isReplaceProject) {
    return projectStore.configs.enableAllSubtitleLanguages
  }

  // ai消除文本翻译的数据
  if (isSegmentEnableLanguages(segment.id)) {
    return true
  }

  const source = findSourceSegment(segment, trackStore.allSegments)

  return (
    isSegmentEnableLanguages(source.pid) || isSegmentEnableLanguages(source.id)
  )
}

// 获取多语言元件的源头元件
export function findSourceSegment(
  segment: SegmentCollection,
  allSegments = getAllSegments(),
): SegmentCollection {
  const source = allSegments.find((t) => t.id === segment.source_segment_id)

  if (!source) return segment

  if (segment.id === segment.source_segment_id) {
    console.error('相同的源头元件，不再获取', segment.id)
    return segment
  }

  return findSourceSegment(source, allSegments)
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function getVideoSubtitle(
  segmentOrId: SegmentCollection | SegmentVideo['id'],
  options?: {
    excludeEliminationSource?: boolean
  },
) {
  const segmentId =
    typeof segmentOrId === 'number' ? segmentOrId : segmentOrId.id
  const trackStore = useTrackStore()

  return trackStore.segments
    .filter(
      (s) =>
        s.source_segment_id > 0 &&
        s.source_segment_id === segmentId &&
        isSegmentSubtitle(s),
    )
    .filter((s) =>
      options?.excludeEliminationSource
        ? s.biz_type !== SourceSegmentIdBizType.Elimination
        : true,
    ) as SegmentText[]
}
// 获取元件在当前语言的真实元件
export function getRealLanguageSegment(
  segment: SegmentCollection,
  lang = DEFAULT_LANGUAGE,
): SegmentCollection {
  const trackStore = useTrackStore()
  const projectStore = useProjectStore()

  if (
    projectStore.isReplaceProject &&
    !isSourceSegmentEnableLanguages(segment)
  ) {
    return segment
  }

  if (lang === DEFAULT_LANGUAGE) {
    return segment
  }

  return (
    trackStore.segments.find(
      (t) => t.source_segment_id === segment.id && t.lang === lang,
    ) ?? segment
  )
}

// 获取字幕元件在当前语言的真实元件
export function getTextRealLanguageSegment(
  segment: SegmentCollection,
  lang = DEFAULT_LANGUAGE,
): SegmentCollection {
  const trackStore = useTrackStore()

  if (lang === DEFAULT_LANGUAGE) {
    return segment
  }

  return (
    trackStore.segments.find(
      (t) => t.source_segment_id === segment.id && t.lang === lang,
    ) ?? segment
  )
}

// 获取元件在当前语言的真实元件 未找不返回undefined
export function getRealLanguageSegmentByLang(
  segment: SegmentCollection,
  lang = DEFAULT_LANGUAGE,
): SegmentCollection | undefined {
  const trackStore = useTrackStore()

  if (lang === DEFAULT_LANGUAGE) {
    return segment
  }

  return trackStore.segments.find(
    (t) => t.source_segment_id === segment.id && t.lang === lang,
  )
}

// 过滤其它语言的数据
export function filterLanguageSegment(segment: SegmentCollection): boolean {
  const projectStore = useProjectStore()

  // 如果是音频，则返回非多语言的数据和多语言的当前语言数据
  if (
    segment.type === SegmentType.Audio &&
    segment.lang &&
    (!projectStore.isReplaceProject || isSourceSegmentEnableLanguages(segment))
  ) {
    return segment.lang === projectStore.configs.activeLanguage
  }

  // if (
  //   !projectStore.isReplaceProject &&
  //   segment.type === SegmentType.Audio &&
  //   segment.lang === '' &&
  //   projectStore.configs.activeLanguage !== DEFAULT_LANGUAGE &&
  //   projectStore.configs.combineIds.includes(segment.id)
  // ) {
  //   return false
  // }

  return segment.lang === ''
}

// 获取其他视频speaker下其他语言的音色
export function getSpeakersLanguagesSpeech(
  id: number,
  speaker: string,
  segment: SegmentCollection,
  lang = DEFAULT_LANGUAGE,
): string {
  const trackStore = useTrackStore()

  const textSegments = trackStore.segments.filter(
    (item) =>
      item.source_segment_id === id &&
      item.id !== segment.id &&
      item.type === SegmentType.Text,
  )
  let langSpeaker = ''
  textSegments.forEach((m) => {
    if (m.speaker === speaker) {
      const s = getRealLanguageSegmentByLang(m, lang)
      langSpeaker = s ? s.speaker : ''
    }
  })
  return langSpeaker
}

export function addChangedSegment(
  id: number,
  options?: {
    autoSave?: boolean
  },
): void {
  const projectStore = useProjectStore()
  const { autoSave = true } = options ?? {}

  if (!projectStore.configs.changedSegmentsIds.some((t) => t === id)) {
    projectStore.configs.changedSegmentsIds.push(id)

    if (autoSave) {
      projectStore.updateProjectConfig()
    }
  }
}

export function removeChangedSegment(id: number): void {
  const projectStore = useProjectStore()
  projectStore.configs.changedSegmentsIds =
    projectStore.configs.changedSegmentsIds.filter((t) => t !== id)
}

export function clearChangedSegment(): void {
  const projectStore = useProjectStore()
  projectStore.configs.changedSegmentsIds = []

  projectStore.updateProjectConfig()
}

export function addChangedLanguagesSegment(id: number): void {
  const projectStore = useProjectStore()

  if (!projectStore.configs.changedLanguagesSegmentsIds.some((t) => t === id)) {
    projectStore.configs.changedLanguagesSegmentsIds.push(id)

    projectStore.updateProjectConfig()
  }
}

export function removeChangedLanguagesSegment(id: number): void {
  const projectStore = useProjectStore()
  projectStore.configs.changedLanguagesSegmentsIds =
    projectStore.configs.changedLanguagesSegmentsIds.filter((t) => t !== id)
}

export function clearChangedLanguagesSegment(): void {
  const projectStore = useProjectStore()
  projectStore.configs.changedLanguagesSegmentsIds = []

  projectStore.updateProjectConfig()
}

/**
 * 添加发生变化的原字幕元件id
 * @description 只允许原字幕的元件id，为了通知该原字幕需要更新配音
 * @param id
 * @param options
 */
export function addChangedDefaultLanguagesSegment(
  id: number,
  options?: { autoSave?: boolean },
): void {
  const { autoSave = true } = options ?? {}
  const projectStore = useProjectStore()

  if (
    !projectStore.configs.changedDefaultLanguagesSegmentsIds.some(
      (t) => t === id,
    )
  ) {
    projectStore.configs.changedDefaultLanguagesSegmentsIds.push(id)

    if (autoSave) {
      projectStore.updateProjectConfig()
    }
  }
}

export function removeChangedDefaultLanguagesSegment(id: number): void {
  const projectStore = useProjectStore()
  projectStore.configs.changedDefaultLanguagesSegmentsIds =
    projectStore.configs.changedDefaultLanguagesSegmentsIds.filter(
      (t) => t !== id,
    )
}

export function clearChangedDefaultLanguagesSegment(): void {
  const projectStore = useProjectStore()
  projectStore.configs.changedDefaultLanguagesSegmentsIds = []
}

/**
 * 添加原语言字幕变更记录
 * @param id
 */
export function addDefaultSubtitleChanged(segment: SegmentText): void {
  // 若不是原字幕，则不处理
  if (segment.lang) {
    return
  }

  // 通知更新原字幕的配音
  addChangedDefaultLanguagesSegment(segment.id, { autoSave: false })

  if (isMultilingualEnabled(segment)) {
    // 通知裂变需要更新
    addChangedSegment(segment.id, { autoSave: false })
  }
}

export interface UpdateSpeakerVoiceOption {
  /** 该值默认继承voice下的性别 */
  selectedGender?: SpeakerVoice['selectedGender']
  voice?: SpeechVoiceModel
  segmentVideoId: SegmentVideo['id']
  speaker: string
  lang: string
  clone: boolean
}

export function updateSpeakerVoice(
  options: UpdateSpeakerVoiceOption & {
    autoSave?: boolean
  },
): void {
  const {
    segmentVideoId,
    clone,
    speaker,
    lang,
    voice,
    autoSave = true,
  } = options

  const projectStore = useProjectStore()

  // 快速替换里面使用传参，视频编辑器里面直接使用通过 speaker 获取的 speakerSource
  const targetSegmentVideoId = projectStore.isReplaceProject
    ? segmentVideoId
    : getSourceIdBySpeaker(speaker, segmentVideoId)

  if (!projectStore.configs.speaker[targetSegmentVideoId]) {
    Vue.set(projectStore.configs.speaker, targetSegmentVideoId, {})
  }

  if (!projectStore.configs.speaker[targetSegmentVideoId]![speaker]) {
    Vue.set(projectStore.configs.speaker[targetSegmentVideoId]!, speaker, {})
  }

  const beforeReplaced =
    projectStore.configs.speaker[targetSegmentVideoId]![speaker]![lang]
  const beforeReplacedSelectedGender =
    beforeReplaced &&
    isSettledSpeakerVoice(beforeReplaced) &&
    beforeReplaced.clone === false
      ? beforeReplaced.selectedGender
      : undefined

  const speakerVoice = createSpeakerVoice({
    clone,
    voice,
    selectedGender: options.selectedGender ?? beforeReplacedSelectedGender,
  })
  Vue.set(
    projectStore.configs.speaker[targetSegmentVideoId]![speaker]!,
    lang,
    speakerVoice,
  )

  if (autoSave) {
    projectStore.updateProjectConfig()
  }
}

export function clearSpeakerVoice(): void {
  const projectStore = useProjectStore()
  projectStore.configs.speaker = {}

  projectStore.updateProjectConfig()
}

export function batchUpdateSpeakerVoice(
  options: UpdateSpeakerVoiceOption[],
  autoSave = true,
): void {
  const projectStore = useProjectStore()

  options.forEach((option) => {
    updateSpeakerVoice({
      ...option,
      autoSave: false,
    })
  })

  if (autoSave) {
    projectStore.updateProjectConfig()
  }
}

// 多语言翻译不支持的语言
export const NOT_SUPPORT_LANG = [
  { name: 'Taglog(tl)', code: 'tl' },
  { name: 'Akan(ak)', code: 'ak' },
  { name: 'Belarusian(be)', code: 'be' },
  { name: 'Bhojpuri(bh)', code: 'bh' },
  { name: 'Cebuano(ceb)', code: 'ceb' },
  { name: 'Esperanto(eo)', code: 'eo' },
  { name: 'Haitian Creole(ht)', code: 'ht' },
  { name: 'Igbo(ig)', code: 'ig' },
  { name: 'Javanese(jw)', code: 'jw' },
  { name: 'Kinyarwanda(rw)', code: 'rw' },
  { name: 'Kurdish(ku)', code: 'ku' },
  { name: 'Malagasy(mg)', code: 'mg' },
  { name: 'Nynorsk(no)', code: 'no' },
  { name: 'Chewa(ny)', code: 'ny' },
  { name: 'Oriya(or)', code: 'or' },
  { name: 'Oromo(om)', code: 'om' },
  { name: 'Punjabi(pa)', code: 'pa' },
  { name: 'Rundi(rn)', code: 'rn' },
  { name: 'Shona(sn)', code: 'sn' },
  { name: 'Tigrinya(ti)', code: 'ti' },
  { name: 'Turkmen(tk)', code: 'tk' },
  { name: 'Uyghur(ug)', code: 'ug' },
  { name: 'Yiddish(yi)', code: 'yi' },
  { name: 'Yoruba(yo)', code: 'yo' },
]

// 音色支持的多语言语言
export const SUPPORT_MULTI_LANG = [
  { name: t('action.unlimitedLanguage', { num: 77 }), code: 'v3' },
  // { name: t('action.unlimitedLanguage', { num: 26 }), code: 'v1' },
]

// 获取全部speaker
export function getConfigsSpeaker(
  speakerConfig?: ProjectConfig['speaker'],
): ReturnType<typeof resolveConfigsSpeaker> {
  const projectStore = useProjectStore()
  const speakers = speakerConfig ?? projectStore.configs.speaker

  return resolveConfigsSpeaker(speakers)
}

export function resolveConfigsSpeaker(
  speakers: ProjectConfig['speaker'],
): Array<keyof SpeakerCollection> {
  const speakerList: string[] = []
  for (const key in speakers) {
    // @ts-expect-error
    speakerList.push(...Object.keys(speakers[key]))
  }

  return speakerList.sort()
}

// 获取自定义speaker
export function getCustomSpeaker(id?: SegmentCollection['id']): string[] {
  const projectStore = useProjectStore()
  const speaker = projectStore.configs.speaker?.[id ?? 0]
  if (speaker) {
    return Object.keys(speaker)
  }
  return []
}

// 根据speaker获取
export function getSourceIdBySpeaker(
  speaker: string,
  fallback?: keyof ProjectConfig['speaker'],
): number {
  const projectStore = useProjectStore()
  let source = fallback ?? 0
  const configsSpeaker = projectStore.configs.speaker
  for (const key in configsSpeaker) {
    if (speaker in configsSpeaker[key]!) {
      source = Number(key)
      break
    }
  }
  return source
}

// 判断speaker是否有设置音色
// 或者用户上传的音色没有被删除
export function hasModelBySpeaker(speaker: string): boolean {
  const projectStore = useProjectStore()
  const configsSpeaker = projectStore.configs.speaker
  let speakerModel = ''
  for (const key in configsSpeaker) {
    if (configsSpeaker[key]![speaker]) {
      if (isSpeakerUserUpload(configsSpeaker[key]![speaker]![''])) {
        speakerModel = projectStore.voiceList.some(
          (voice) => voice.id === configsSpeaker[key]![speaker]!['']!.voice_id,
        )
          ? 'true'
          : ''
        break
      }

      speakerModel = configsSpeaker[key]![speaker]!['']!.model || ''
      break
    }
  }
  return !!speakerModel
}

// 获取原语言单个speaker音色设置
export function getModelBySpeaker(speaker: string, lang = '') {
  const projectStore = useProjectStore()
  const configsSpeaker = projectStore.configs.speaker
  let speakerModel: any = {}
  for (const key in configsSpeaker) {
    if (configsSpeaker[key]![speaker]) {
      speakerModel = configsSpeaker[key]![speaker]![lang]!
      break
    }
  }
  return speakerModel
}

/**
 * 判断当前元件是否都不引用该资源，没有就把这个id从数组中删除s
 * @param id asset_id
 */
export function removeLanguagesReplaceAssetId(
  id: SegmentMaterial['asset_id'],
): void {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()
  const allSegments = trackStore.allSegments

  if (
    projectStore.configs.languagesReplaceAssetIds.includes(id) &&
    !allSegments.map((t) => t.asset_id).some((t) => t === id)
  ) {
    projectStore.configs.languagesReplaceAssetIds =
      projectStore.configs.languagesReplaceAssetIds.filter(
        (item) => item !== id,
      )
  }
}

/**
 * 优先拿 v3 的对应性别数据
 * 其次拿 v1 的对应性别数据
 * 最后拿 language 对应的数据
 * 都没有，返回空
 * @param SpeechVoiceModelList 返回的大模型音色列表
 * @param gender 性别
 * @param language 语言
 * @param speaker 角色
 */
export function getSpeakerDefaultVoice({
  speechVoiceModelList,
  gender,
  language,
  speaker,
}: {
  speechVoiceModelList: SpeechVoiceModel[]
  gender: string
  language: string
  speaker: string
}): SpeechVoiceModel | null {
  const v3VoiceList = speechVoiceModelList.filter((voice) => {
    return (
      voice.tag === 'v3' &&
      (!voice.secondary_locale_list ||
        !voice.secondary_locale_list.length ||
        !language ||
        (voice.secondary_locale_list &&
          voice.secondary_locale_list.length &&
          language &&
          voice.secondary_locale_list.some((locale) => {
            return locale.toLowerCase().indexOf(language.toLowerCase()) !== -1
          })))
    )
  })

  const v1VoiceList = speechVoiceModelList.filter((voice) => {
    return (
      voice.tag === 'v1' &&
      (!voice.secondary_locale_list ||
        !voice.secondary_locale_list.length ||
        !language ||
        (voice.secondary_locale_list &&
          voice.secondary_locale_list.length &&
          language &&
          voice.secondary_locale_list.some((locale) => {
            return locale.toLowerCase().indexOf(language.toLowerCase()) !== -1
          })))
    )
  })
  const currentLanguageVoiceList = speechVoiceModelList.filter((voice) => {
    return (
      !language ||
      voice.locale.toLowerCase().indexOf(language.toLowerCase()) !== -1
    )
  })
  let voiceList

  if (v3VoiceList.length) {
    voiceList = v3VoiceList
  } else if (v1VoiceList.length) {
    voiceList = v1VoiceList
  } else {
    voiceList = currentLanguageVoiceList
  }
  const targetVoiceList = voiceList.filter((voice) => {
    return gender !== 'Neutral' ? voice.gender === gender : true
  })
  if (targetVoiceList.length) {
    const idx = Number(speaker.split('_')[1])
    return targetVoiceList[idx % targetVoiceList.length]
  }
  return null
}

export function resolveDefaultConfig(): ProjectConfig {
  return {
    unLinkageRatio: [],
    isRatioLinkage: false,
    enableLanguagesSegmentIds: [],
    activeLanguage: DEFAULT_LANGUAGE,
    changedSegmentsIds: [],
    changedLanguagesSegmentsIds: [],
    changedDefaultLanguagesSegmentsIds: [],
    speaker: {},
    mainRatio: RatioType['9:16'] as RatioTypeUnion,
    languagesReplaceAssetIds: [],
    templateRatioId: 0,
    aiRemoveAssetIds: [],
    aiRemove: {},
    task: [],
    combineIds: [],
    enableAllSubtitleLanguages: false,
  } as ProjectConfig
}

export function updateSpeakerVoiceByLab(options: {
  id: VoiceItem['id']
  segmentVideoId: SegmentCollection['id']
  speaker: string
  language: string
  autoSave?: boolean
}): void {
  const projectStore = useProjectStore()

  const { id, segmentVideoId, speaker, language, autoSave = true } = options

  const targetSegmentVideoId = projectStore.isReplaceProject
    ? segmentVideoId
    : getSourceIdBySpeaker(speaker, segmentVideoId)

  Vue.set(
    projectStore.configs.speaker[targetSegmentVideoId]![speaker]!,
    language,
    {
      voice_id: id,
      type: SpeakerType.UserUpload,
      clone: true,
    },
  )

  if (autoSave) {
    projectStore.updateProjectConfig()
  }
}

export function isSpeakerUserUpload(
  speaker: Speaker,
): speaker is SpeakerUserUpload {
  return speaker && 'type' in speaker && speaker.type === SpeakerType.UserUpload
}
